import React from 'react';
import T from './theme';
import CollapsibleCard from './CollapsibleCard';
import InfoBubble from './InfoBubble';
import { runTrimpPerMinute, TRIMP_RATE_HRR, trimpRateLegend } from './trimpRates';

const WATAP_INFO =
  'Weather and terrain adjusted pace. Cool-flat equivalent of this run: GAP divided by heat (the soup) and sun.';

function TrimpInfo({ trimp, minutes }) {
  const rates = trimpRateLegend();
  const rows = [
    ['Easy', TRIMP_RATE_HRR.easy, rates.easy],
    ['Moderate', TRIMP_RATE_HRR.moderate, rates.moderate],
    ['Hard', TRIMP_RATE_HRR.hard, rates.hard],
  ];
  const thisRun = runTrimpPerMinute(trimp, minutes);
  return (
    <>
      Banister training impulse: minutes × heart-rate intensity. Session load, not heat-adjusted.
      <div style={{ marginTop: 8 }}>
        {rows.map(([name, hrr, rate]) => (
          <div
            key={name}
            style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}
          >
            <span>
              {name} {Math.round(hrr * 100)}% HRR
            </span>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{rate.toFixed(2)} /min</span>
          </div>
        ))}
        {thisRun !== null && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 12,
              marginTop: 4,
              fontWeight: 600,
            }}
          >
            <span>This run</span>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{thisRun.toFixed(2)} /min</span>
          </div>
        )}
      </div>
    </>
  );
}

// House numbers only (clock, GAP, WaTaP, HR, %LTHR, Banister TRIMP). WaTaP is
// GAP / H — the cool-flat equivalent effort, weather and terrain adjusted. It
// is omitted when we do not know the weather for that run rather than guessed.
// TRIMP is load, not a 0-100 training score and not heat-scaled, and nothing
// here is medical clearance.

function whenLabel(start) {
  if (!start) return null;
  const d = new Date(start);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString([], {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function verdictLine(lastRun) {
  const verdict = lastRun.verdict;
  if (verdict !== 'snappy' && verdict !== 'slow') return null;
  const delta = lastRun.delta_s;
  if (delta === null || delta === undefined) return null;
  const secs = Math.abs(Math.round(delta));
  if (!secs) return null;
  // Match what the backend actually compared against. It only measures WaTaP
  // against easy when easy is itself a cool-flat pace; otherwise it is GAP.
  let basis = 'easy pace';
  if (lastRun.watap && lastRun.easy_is_cool_flat) basis = 'easy WaTaP';
  else if (lastRun.gap) basis = 'easy GAP';
  return `${secs} s/mi ${verdict === 'snappy' ? 'faster' : 'slower'} than ${basis}`;
}

function Metric({ label, value, info, align }) {
  return (
    <span style={{ display: 'inline-flex', gap: 4, alignItems: 'baseline' }}>
      <span style={{ color: T.color.textLight, fontSize: T.font.xs }}>
        {info ? (
          <InfoBubble label={label} align={align}>
            {info}
          </InfoBubble>
        ) : (
          label
        )}
      </span>
      <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{value}</span>
    </span>
  );
}

function MetricRow({ items }) {
  const shown = items.filter((m) => m.value !== null && m.value !== undefined && m.value !== '');
  if (!shown.length) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', marginTop: 6 }}>
      {shown.map((m) => (
        <Metric key={m.label} label={m.label} value={m.value} info={m.info} align={m.align} />
      ))}
    </div>
  );
}

export default function LatestRunCard({ lastRun, inspiration }) {
  const when = lastRun ? whenLabel(lastRun.start) : null;
  const headline = lastRun
    ? [
        lastRun.workout_type_label,
        Number.isFinite(lastRun.miles) ? `${lastRun.miles.toFixed(2)} mi` : null,
        Number.isFinite(lastRun.minutes) ? `${Math.round(lastRun.minutes)} min` : null,
      ].filter(Boolean).join(' · ')
    : '';
  const snappy = lastRun ? verdictLine(lastRun) : null;

  // Open-by-default and first-in-order live in useCardPrefs, which is what the
  // dashboard actually drives this card with.
  return (
    <CollapsibleCard title="Latest run" accentColor={T.color.accentRun}>
      {lastRun ? (
        <>
          <div style={{ marginTop: 4, fontWeight: 600 }}>
            {[lastRun.name, when].filter(Boolean).join(' · ')}
          </div>
          {headline && (
            <div style={{ color: T.color.textMuted, fontSize: T.font.sm, marginTop: 2 }}>{headline}</div>
          )}
          <MetricRow
            items={[
              { label: 'Clock', value: lastRun.pace },
              { label: 'GAP', value: lastRun.gap },
              { label: 'WaTaP', value: lastRun.watap, info: WATAP_INFO },
              { label: 'Easy', value: lastRun.easy },
            ]}
          />
          <MetricRow
            items={[
              { label: 'HR', value: lastRun.hr ? String(Math.round(lastRun.hr)) : null },
              {
                label: '% LTHR',
                value:
                  lastRun.pct_lthr === null || lastRun.pct_lthr === undefined
                    ? null
                    : `${Math.round(lastRun.pct_lthr * 100)}%`,
              },
              {
                label: 'TRIMP',
                value:
                  lastRun.trimp === null || lastRun.trimp === undefined
                    ? null
                    : String(Math.round(lastRun.trimp)),
                align: 'end',
                info: (
                  <TrimpInfo trimp={lastRun.trimp} minutes={lastRun.minutes} />
                ),
              },
            ]}
          />
          {snappy && (
            <div style={{ color: T.color.textLight, fontSize: T.font.sm, marginTop: 6 }}>{snappy}</div>
          )}
        </>
      ) : (
        <p style={{ marginTop: 4 }}>No imported runs yet.</p>
      )}
      {inspiration && (
        <p style={{ color: T.color.textMuted, fontStyle: 'italic', margin: '10px 0 0' }}>{inspiration}</p>
      )}
    </CollapsibleCard>
  );
}
